import { ExtraScopeEnum } from '@koob/enums';
import {
  getExtraAmount,
  isExtraOfferedForPeriod,
  isExtraStateAvailable,
  isSameExtraOccurrence,
  isUnderParentOccurrence,
  sumExtrasAmountPerPax,
} from '@koob/utils';

// The additional-services maths of a program day, out of the component
// (US-064): what is selected, what a service costs, and which lines a chip's
// cross removes.

// KD-3581 §4 — the band lists what is selected, so the selection itself is
// what is derived here; the count in its subtitle is just its length.
export function getServiceSelections({
  experience,
  experienceExtras,
  accomodationExtras,
  selectedExtras,
  extrasPrices,
}: {
  experience: any;
  experienceExtras: any[];
  accomodationExtras: string[];
  selectedExtras: any;
  extrasPrices?: any[];
}) {
  // Global services are spread over the days of the instance, each on the day
  // it was filed on: the aggregate is what the band shows, on the first day.
  const globalSelectedExtras = experienceExtras.filter(
    (extra) =>
      !accomodationExtras.includes(extra.id) &&
      extra.scope === ExtraScopeEnum.GLOBAL,
  );

  const daySelectedExtras = (
    Array.isArray(selectedExtras) ? selectedExtras : []
  ).filter((extra: any) => extra.scope === ExtraScopeEnum.DAY);

  // What the band announces before anything is picked: the offer, not the
  // selection. The global side is the experience's own extras minus those a
  // program already suggests for one of its days — the split the modal makes.
  //
  // KD-3749 — only what can be used on these dates counts as offered: a
  // service the modal leaves out must not be announced here.
  const globalAvailableCount = (experience?.extras ?? []).filter(
    (extra: any) =>
      !accomodationExtras.includes(extra.id) &&
      !experience?.programs?.some((program: any) =>
        program?.suggestedExperiences?.some((exp: any) => exp.id === extra.id),
      ) &&
      isExtraStateAvailable(extra) &&
      isExtraOfferedForPeriod(extrasPrices, extra.id),
  ).length;

  return { globalSelectedExtras, daySelectedExtras, globalAvailableCount };
}

// `getExtraAmount` moved to `@koob/utils` (KD-3744, shared with the per-pax
// helpers below); re-exported here since every caller in this package still
// imports it from this module.
export { getExtraAmount };

export const sumExtrasPrice = (extras: any[]): number =>
  extras.reduce((total, extra) => total + getExtraAmount(extra), 0);

// KD-3744 — the band's per-traveller share: services priced in different
// currencies cannot be added into one share, so it is left out then.
export const getExtrasAmountPerPax = (extras: any[]): number | null => {
  const currencies = new Set(
    extras.map((extra) => extra?.currency).filter(Boolean),
  );

  return currencies.size > 1 ? null : sumExtrasAmountPerPax(extras);
};

// The band total is priced like its own chips: a service carries its own
// currency, and the experience price is only the fallback — it can be
// `mismatch`, which no formatter understands. Services that disagree keep
// that fallback, so a single currency is never claimed for a mixed sum.
export const getExtrasCurrency = (extras: any[], fallback: string): string => {
  const currencies = [
    ...new Set(extras.map((extra) => extra?.currency).filter(Boolean)),
  ];

  return currencies.length === 1 ? (currencies[0] as string) : fallback;
};

// Same predicate as the modal's own delete: a parent takes its secondary
// options down with it, a child leaves the parent standing.
//
// KD-3650 / FR-4 — a chip is one OCCURRENCE. Matching on `id` alone named the
// whole service, so the cross of Nº 1 removed Nº 2 along with it; the
// occurrence key is what narrows the match down to the line clicked, and a
// removed occurrence takes down only the sub-options ticked under itself.
export const removeServiceFromExtras = (
  currentExtras: any[],
  target: any,
): any[] =>
  currentExtras.filter((extra) => {
    const isSameInstance =
      extra.experienceId === target?.experienceId &&
      extra.scope === target?.scope;

    if (target?.parentExtraId) {
      return !(
        isSameInstance &&
        extra.id === target.id &&
        (extra.parentExtraId ?? null) === target.parentExtraId &&
        isSameExtraOccurrence(extra, target)
      );
    }

    return !(
      isSameInstance &&
      ((extra.id === target?.id && isSameExtraOccurrence(extra, target)) ||
        (extra.parentExtraId === target?.id &&
          isUnderParentOccurrence(extra, target)))
    );
  });
